import { loadBannerRegionCatalogue } from './bannerRegions';
import { getViewerRegion } from './bannerLocation';
import axios from "axios";
import { supabase } from "./supabaseClient";
import { isNativeAndroid, isNativeIOS, runNativePurchase, runNativeApplePurchase } from "./nativeBilling";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUCKET = "banner-ads";

async function authHeader() {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Optimise the uploaded banner to a 3:1 900×300 WebP WITHOUT cropping: the full
// image is fitted (contain) inside the slot and any letterbox uses the ad-slot
// background so a true 3:1 upload fills edge-to-edge with no padding, and an
// off-ratio upload is never cropped/stretched. Feeds use ONLY display.
async function toDisplayWebp(file) {
  const bitmap = await createImageBitmap(file);
  const TW = 900, TH = 300;
  const canvas = document.createElement("canvas");
  canvas.width = TW;
  canvas.height = TH;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#0e0e0e";
  ctx.fillRect(0, 0, TW, TH);
  const scale = Math.min(TW / bitmap.width, TH / bitmap.height);
  const w = bitmap.width * scale, h = bitmap.height * scale;
  ctx.drawImage(bitmap, (TW - w) / 2, (TH - h) / 2, w, h);
  return new Promise((r) => canvas.toBlob(r, "image/webp", 0.82));
}

const MAX_BANNER_BYTES = 5 * 1024 * 1024;
const ACCEPTED_BANNER_TYPES = ["image/jpeg", "image/png", "image/webp"];

export function validateBannerFile(file) {
  if (!file) return "Please choose a banner image.";
  if (!ACCEPTED_BANNER_TYPES.includes(file.type)) return "Please upload a JPG, PNG or WebP image.";
  if (file.size > MAX_BANNER_BYTES) return "Image must be 5 MB or smaller.";
  return null;
}

export async function uploadBannerImage(file) {
  const err = validateBannerFile(file);
  if (err) throw new Error(err);
  const { data } = await supabase.auth.getSession();
  const uid = data?.session?.user?.id;
  if (!uid) throw new Error("Please log in.");
  const ts = Date.now();
  const display = await toDisplayWebp(file);
  const dispPath = `${uid}/${ts}-disp.webp`;
  const up1 = await supabase.storage.from(BUCKET).upload(dispPath, display, {
    contentType: "image/webp", upsert: false,
  });
  if (up1.error) throw new Error("Image upload failed.");
  // The full-size original is not used by any feature (all banner rendering uses
  // the 900×300 display WebP), so it is no longer stored — only the optimised WebP
  // is kept. Both path fields point at that single file (backend/schema unchanged).
  return { imagePath: dispPath, displayImagePath: dispPath };
}

export async function getBannerRegions({ withStatus = false } = {}) {
  const catalogue = await loadBannerRegionCatalogue(supabase);
  return withStatus ? catalogue : catalogue.regions;
}

export async function startBannerCheckout({ bannerAdId, imagePath, displayImagePath, destinationUrl, targetCountryCode, targetRegionCode }) {
  const headers = await authHeader();
  // Native wrappers: prepare the draft banner server-side (no Stripe), then buy the
  // 'banner_ad_7_day' product; the server activates it on verify. iOS is checked
  // first (its wrapper UA can also match Android).
  if (isNativeIOS()) {
    const { data } = await axios.post(
      `${API}/banner/checkout`,
      {
        banner_ad_id: bannerAdId || null,
        image_path: imagePath,
        display_image_path: displayImagePath,
        destination_url: destinationUrl,
        target_country_code: targetCountryCode,
        target_region_code: targetRegionCode,
        origin_url: window.location.origin,
        native: true,
      },
      { headers }
    );
    if (data?.admin_free) return data; // admins activate free without billing
    await runNativeApplePurchase("app.roadrnr.banner_ad_7_day", { banner_ad_id: data.banner_ad_id });
    return { native: true, ok: true };
  }
  if (isNativeAndroid()) {
    const { data } = await axios.post(
      `${API}/banner/checkout`,
      {
        banner_ad_id: bannerAdId || null,
        image_path: imagePath,
        display_image_path: displayImagePath,
        destination_url: destinationUrl,
        target_country_code: targetCountryCode,
        target_region_code: targetRegionCode,
        origin_url: window.location.origin,
        native: true,
      },
      { headers }
    );
    if (data?.admin_free) return data; // admins activate free without billing
    await runNativePurchase("banner_ad_7_day", { banner_ad_id: data.banner_ad_id });
    return { native: true, ok: true };
  }
  const { data } = await axios.post(
    `${API}/banner/checkout`,
    {
      banner_ad_id: bannerAdId || null,
      image_path: imagePath,
      display_image_path: displayImagePath,
      destination_url: destinationUrl,
      target_country_code: targetCountryCode,
      target_region_code: targetRegionCode,
      origin_url: window.location.origin,
    },
    { headers }
  );
  return data; // { checkout_url, session_id } OR { admin_free: true }
}

export async function confirmBannerPayment(sessionId) {
  try {
    const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
    return data;
  } catch {
    return null;
  }
}

export async function getMyBanners() {
  const headers = await authHeader();
  const { data } = await axios.get(`${API}/banner/mine`, { headers });
  return data;
}

export async function updateBanner(id, { destinationUrl, imagePath, displayImagePath } = {}) {
  const headers = await authHeader();
  const body = {};
  if (destinationUrl !== undefined) body.destination_url = destinationUrl;
  if (imagePath && displayImagePath) {
    body.image_path = imagePath;
    body.display_image_path = displayImagePath;
  }
  const { data } = await axios.patch(`${API}/banner/${id}`, body, { headers });
  return data;
}

export async function endBanner(id) {
  const headers = await authHeader();
  const { data } = await axios.post(`${API}/banner/${id}/end`, {}, { headers });
  return data;
}

export async function deleteBanner(id) {
  const headers = await authHeader();
  const { data } = await axios.delete(`${API}/banner/${id}`, { headers });
  return data;
}

export async function getIsAdmin() {
  try {
    const headers = await authHeader();
    if (!headers.Authorization) return false;
    const { data } = await axios.get(`${API}/me/is-admin`, { headers });
    return !!data?.is_admin;
  } catch {
    return false;
  }
}

const activeBannerRequests = new Map();
export async function getActiveBanners() {
  const params = await getViewerRegion();
  const key = JSON.stringify(params);
  const existing = activeBannerRequests.get(key);
  if (existing && Date.now() - existing.at < 60000) return existing.promise;
  const promise = axios.get(`${API}/banner/active`, { params: params || {} })
    .then(({data}) => data).catch(e => { activeBannerRequests.delete(key); throw e; });
  if (activeBannerRequests.size > 4) activeBannerRequests.clear();
  activeBannerRequests.set(key, {at:Date.now(),promise});
  return promise;
}

// ---- Admin (existing Admin_Users authorization; 403 if not admin) ----
export const EVENT_REMOVE_REASONS = [
  "Inappropriate content", "Misleading/incorrect event", "Duplicate/spam",
  "Not suitable for RoadRnr", "Breaches RoadRnr rules", "Other",
];
export const BANNER_REMOVE_REASONS = [
  "Inappropriate content", "Misleading ad", "Unsafe/invalid link",
  "Copyright/IP issue", "Not suitable for RoadRnr audience",
  "Breaches advertising rules", "Other",
];

// params: { status, q, page, page_size } for the paged, searchable list over every
// event; with no params the backend returns the legacy newest-100 array.
export async function adminGetEvents(params) {
  const headers = await authHeader();
  const { data } = await axios.get(`${API}/admin/events`, { headers, params });
  return data;
}
export async function adminModerateEvent(id, action, reason) {
  const headers = await authHeader();
  const { data } = await axios.post(`${API}/admin/events/${id}/moderate`, { action, reason }, { headers });
  return data;
}
export async function adminGetBanners() {
  const headers = await authHeader();
  const { data } = await axios.get(`${API}/admin/banners`, { headers });
  return data;
}
export async function adminModerateBanner(id, action, reason) {
  const headers = await authHeader();
  const { data } = await axios.post(`${API}/admin/banners/${id}/moderate`, { action, reason }, { headers });
  return data;
}

export async function adminGetReports() {
  const headers = await authHeader();
  const { data } = await axios.get(`${API}/admin/reports`, { headers });
  return data;
}

export async function adminDismissReports(eventId) {
  const headers = await authHeader();
  const { data } = await axios.post(`${API}/admin/reports/${eventId}/dismiss`, {}, { headers });
  return data;
}

export async function adminGrantListingCredits(email, credits, reference) {
  const headers = await authHeader();
  const { data } = await axios.post(
    `${API}/admin/listing/grant`,
    { email, credits, reference },
    { headers }
  );
  return data;
}

